import { CodeBracketSquareIcon } from "@components/library/Icons";
import { ContextMenuListItem } from "@components/library/Lists/ContextMenuListItem";
import { VariableIcon } from "@heroicons/react/outline";
import * as Popover from "@radix-ui/react-popover";
import { EditorState, SelectionState } from "draft-js";
import { Dispatch, SetStateAction, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEventListener } from "usehooks-ts";
import { InputTag, ToolTag } from "./VariableTags";
import { addTextAtCursor, deleteTextAtCursor } from "./utils";
import { isOrgToolConfig, OrgToolConfig } from "@/services/v4.service";
import { getSignature, TemplateTool } from "@/lib/prompt-editor-utils";
import { ToolFunction } from "@/types/app/tool";
import { FileLabelWithPopover } from "@components/library/Entities/FileLabelWithPopover";
import { classNames } from "@/lib/utils";
import { EntityIcon } from "@components/library/Entities/EntityIcon";
import { FileWithSchema } from "@/types/app/file";
import { useMonitoringFile } from "@/context/MonitoringFileContext";
import { capitalizedFileType } from "@/services/files.service";

export interface InputSuggestion {
  type: "input";
  name: string;
  description?: string;
  isOrgTool: false;
}
type ToolSuggestion =
  | {
      type: "tool";
      tool: FileWithSchema;
      toolSchema: ToolFunction;
      signature: string | null;
      isOrgTool: false;
    }
  | {
      type: "tool";
      tool: OrgToolConfig;
      toolSchema: ToolFunction;
      signature: string | null;
      isOrgTool: true;
    };
type Suggestion = (InputSuggestion | ToolSuggestion) & {
  Icon: (props: React.ComponentProps<"svg">) => JSX.Element;
};

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  editorState: EditorState;
  setEditorState: (editorState: EditorState) => void;
  /** List of available tools */
  availableTools?: TemplateTool[];
  /** List of available input names */
  availableVariables?: InputSuggestion[];
  /** Toggles whether this responds to keystrokes */
  enabled: boolean;
}

type Position = { top: number; left: number };

const AutocompletePanel = ({
  open,
  setOpen,
  editorState,
  setEditorState,
  enabled,
  availableTools,
  availableVariables,
}: Props) => {
  const { file } = useMonitoringFile();

  const [positionStyles, setPositionStyles] = useState<Position | null>(null);

  const selectionState = editorState.getSelection();

  // TODO: This is fixed relative to the viewport, and it doesn't update when the user scrolls, or the layout changes.
  // We should fix this. Ideas:
  // Look at how @mentions are done https://www.draft-js-plugins.com/plugin/mention
  // Use relative position to the editor, and use Radix portal which automatically moves the element to the trigger.
  useEffect(() => {
    const positionStyles = getPositionStyles(selectionState);
    if (positionStyles !== null) {
      setPositionStyles(positionStyles);
    }
  }, [selectionState]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [typedString, setTypedString] = useState("");

  const handleClose = useCallback(() => {
    setOpen(false);
    setSelectedIndex(0);
    setTypedString("");
  }, [setOpen]);

  const suggestions: Suggestion[] = useMemo(() => {
    const filteredInputs = availableVariables?.filter((input) => input.name.startsWith(typedString)) || [];
    const filteredTools: ToolSuggestion[] =
      availableTools
        ?.map((tool) => {
          const isOrgTool = isOrgToolConfig(tool);
          return isOrgTool
            ? ({
                type: "tool",
                tool,
                signature: getSignature(tool),
                isOrgTool,
                toolSchema: tool.config,
              } satisfies ToolSuggestion)
            : ({
                type: "tool",
                tool,
                signature: getSignature(tool),
                isOrgTool,
                toolSchema: tool.schema,
              } satisfies ToolSuggestion);
        })
        .filter((tool) => tool.signature?.startsWith(typedString)) ?? [];
    return [...filteredInputs, ...filteredTools].map((suggestion) => {
      let Icon;
      switch (suggestion.type) {
        case "input":
          Icon = VariableIcon;
          break;
        case "tool":
          if (suggestion.isOrgTool) {
            Icon = CodeBracketSquareIcon;
          } else {
            // eslint-disable-next-line react/display-name
            Icon = (props: any) => (
              <div
                {...props}
                className={classNames(
                  props.className,
                  "flex items-center justify-center",
                  // flex and justify to better align. Otherwise alignment is slightly off.
                  // This icon is a filled one and so looks better slightly smaller.
                  // The ContextMenuListItem below renders the icons with h-18 w-18
                  // and adds some custom margins, which we should pass through here for alignment.
                )}
              >
                <EntityIcon type={suggestion.tool.type} size={14} />
              </div>
            );
          }
          break;
      }
      return {
        ...suggestion,
        Icon,
      };
    });
  }, [availableVariables, availableTools, typedString]);

  const [displayOrgTools, setDisplayOrgTools] = useState(false);
  const displayedSuggestions = suggestions.filter((suggestion) => displayOrgTools || !suggestion.isOrgTool);
  if (displayedSuggestions.length === 0) {
    // Add example variable
    // Mainly avoids showing only "Show legacy tools..." item (or empty panel).
    // Also think this is helpful anyway.
    displayedSuggestions.push({
      type: "input",
      name: "example_variable",
      isOrgTool: false,
      Icon: VariableIcon,
    });
  }
  const handleSuggestionSelection = useCallback(
    (suggestion: Suggestion) => {
      if (suggestion === undefined) {
        return;
      }
      const newEditorState = addToEditorState(suggestion, typedString.length, editorState);
      setEditorState(newEditorState);
      handleClose();
    },
    [editorState, handleClose, setEditorState, typedString.length],
  );

  const keydownEventHandler = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) {
        return;
      }
      if (event.key === "{") {
        const { text, offset } = getBlockTextAndOffset(editorState);
        if (text[offset - 1] === "{") {
          setOpen(true);
          return;
        }
      }

      // Manually trigger autocomplete box with ctrl-space.
      if (event.ctrlKey && event.key === " ") {
        const { text, offset } = getBlockTextAndOffset(editorState);
        if (text.slice(0, offset).includes("{{")) {
          // Try autocomplete from last {{
          const split = text.slice(0, offset).split("{{");
          const textOnRightOfBracket = split[split.length - 1];
          setTypedString(textOnRightOfBracket);
          setOpen(true);
          return;
        }
      }
      if (!open) {
        return;
      }

      if (event.key === "Backspace" || event.key === "ArrowLeft") {
        if (typedString.length > 0) {
          setTypedString((typedString) => typedString.slice(0, typedString.length - 1));
        } else {
          handleClose();
        }
      }
      if (event.key === "ArrowRight") {
        const { text, offset } = getBlockTextAndOffset(editorState);
        setTypedString((typedString) => typedString + text[offset]);
      }

      if (event.key === "Escape") {
        handleClose();
      } else if (event.key === "ArrowUp") {
        setSelectedIndex((selectedIndex) => Math.max(selectedIndex - 1, 0));
      } else if (event.key === "ArrowDown") {
        setSelectedIndex((selectedIndex) =>
          Math.min(
            selectedIndex + 1,
            displayedSuggestions.length -
              1 +
              // Add one for "Show legacy tools" item
              (!displayOrgTools ? 1 : 0),
          ),
        );
      }
      if (event.key === "Enter" || event.key === "Tab") {
        if (selectedIndex > displayedSuggestions.length - 1) {
          setDisplayOrgTools(true);
        } else {
          handleSuggestionSelection(displayedSuggestions[selectedIndex]);
        }
      }
      if (event.key.length === 1) {
        setTypedString((typedString) => `${typedString}${event.key}`);
        setSelectedIndex(0);
      }
    },
    [
      editorState,
      enabled,
      handleSuggestionSelection,
      handleClose,
      open,
      selectedIndex,
      setOpen,
      displayedSuggestions,
      typedString.length,
    ],
  );

  useEventListener("keydown", keydownEventHandler);

  useEventListener("mousedown", (event) => {
    if (open) {
      const isInside = (event.target as any).closest(".hl-suggestion") !== null;
      if (!isInside) {
        handleClose();
      }
    }
  });

  useEffect(() => {
    if (suggestions.length === 0) {
      handleClose();
    }
  }, [handleClose, selectedIndex, suggestions]);

  if (!open) {
    return null;
  }

  const selectedSuggestion: Suggestion | undefined = displayedSuggestions[selectedIndex];

  return (
    <Popover.Root defaultOpen>
      <Popover.Portal>
        <div style={positionStyles || undefined} className="fixed z-10 mt-24 flex animate-fade-in-down items-start">
          {/* Suggestions list */}
          <div className="w-240 overflow-hidden rounded-ms border border-stroke-base-3 bg-white shadow-1">
            <div className="flex max-h-[200px] flex-col overflow-auto">
              {displayedSuggestions.map((suggestion, i) => (
                <AutocompleteSuggestion
                  key={i}
                  suggestion={suggestion}
                  typedString={typedString}
                  active={selectedSuggestion === suggestion}
                  onClick={() => {
                    handleSuggestionSelection(suggestion);
                  }}
                  onPointerEnter={() => {
                    // Cannot use `i` here as that's the index in `displayedSuggestions`, not `suggestions`.
                    setSelectedIndex(suggestions.findIndex((s) => s === suggestion));
                  }}
                />
              ))}
              {!displayOrgTools && suggestions.some((suggestion) => suggestion.isOrgTool) && (
                <ListItem
                  onClick={() => setDisplayOrgTools(true)}
                  active={selectedIndex > displayedSuggestions.length - 1}
                  onPointerEnter={() => {
                    setSelectedIndex(displayedSuggestions.length);
                  }}
                >
                  <div className="ml-8 text-text-base-3">Show legacy tools...</div>
                </ListItem>
              )}
            </div>
          </div>
          {/* Details box on the right */}
          {selectedSuggestion && (
            <div className="w-240 rounded-ms border border-stroke-base-3 bg-white font-sans shadow-1">
              {selectedSuggestion.type === "input" && (
                <div className="flex flex-col gap-8 p-8">
                  <div className="flex items-center justify-between gap-4">
                    <InputTag>{selectedSuggestion.name}</InputTag>
                    {selectedSuggestion.Icon && <selectedSuggestion.Icon className="h-16 w-16 text-icon-base-3" />}
                  </div>
                  <div className="text-pretty text-11-14 text-text-base-2">
                    The value for this <span className="font-bold">input</span> will need to be supplied when running
                    the {capitalizedFileType(file?.type ?? "prompt")}.
                  </div>
                </div>
              )}
              {selectedSuggestion.type === "tool" && (
                <div className="flex flex-col gap-8 p-8">
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-13-16">{selectedSuggestion.tool.name}</div>
                    {selectedSuggestion.Icon && <selectedSuggestion.Icon className="h-16 w-16 text-icon-base-3" />}
                  </div>
                  {selectedSuggestion.toolSchema.description && (
                    <div className="whitespace-pre-wrap text-11-14 text-gray-700">
                      {selectedSuggestion.toolSchema.description}
                    </div>
                  )}
                  {isOrgToolConfig(selectedSuggestion.tool) ? (
                    <></>
                  ) : (
                    <FileLabelWithPopover file={selectedSuggestion.tool} size={24} displayFile />
                    // Even if we find a way to style the Version popover well, it doesn't contain the schema description.
                    // <VersionPopover
                    //   file={selectedSuggestion.tool}
                    //   onFileChange={(updatedFile) => {
                    //     // refetchFiles(updatedFile);
                    //   }}
                    //   isEditable={false}
                    //   showOpenButton={false}
                    // />
                  )}
                  <hr className="border-stroke-base-3" />
                  {selectedSuggestion.isOrgTool ? (
                    <div className="text-pretty text-11-14 text-text-base-2">
                      <span className="font-medium italic">Deprecated.</span> Link the tool to this{" "}
                      {capitalizedFileType(file?.type ?? "prompt")} explicitly for better control over the tool version
                      used.
                    </div>
                  ) : (
                    <div className="text-pretty text-11-14 text-text-base-2">
                      Humanloop will run this <span className="font-bold">template tool</span> when running the{" "}
                      {capitalizedFileType(file?.type ?? "prompt")}. The tool's output will be inserted here.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {selectedSuggestion?.type === "input" && selectedSuggestion?.description && (
            <div className="max-w-[300px] rounded-ms border border-oldgray-400 bg-white font-sans shadow-1">
              <div className="flex flex-col gap-8 p-8">
                <div className="flex items-center justify-between gap-4">
                  <InputTag>{selectedSuggestion.name}</InputTag>
                  {selectedSuggestion.Icon && <selectedSuggestion.Icon className="h-16 w-16 text-icon-base-3" />}
                </div>
                <div className="text-11-14 text-gray-700">{selectedSuggestion.description}</div>
              </div>
            </div>
          )}
        </div>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default memo(AutocompletePanel);

const AutocompleteSuggestion = ({
  suggestion,
  typedString,
  active,
  onClick,
  onPointerEnter,
}: {
  suggestion: Suggestion;
  typedString: string;
  active: boolean;
  onClick: () => void;
  onPointerEnter: () => void;
}) => {
  return (
    <ListItem
      active={active}
      onClick={onClick}
      size={32}
      IconRight={suggestion.Icon}
      className="hl-suggestion"
      onPointerEnter={onPointerEnter}
    >
      {suggestion.type === "input" && (
        <span>
          <InputTag>
            <span className="font-bold text-black">{typedString}</span>
            <span className="text-gray-700">{suggestion.name.slice(typedString.length)}</span>
          </InputTag>
        </span>
      )}

      {suggestion.type === "tool" &&
        // `suggestion` is guaranteed to have a signature due to filter above. Doing check again mainly for TypeScript.
        suggestion.signature && (
          <span className="">
            <ToolTag>
              <span className="font-bold text-black">{typedString}</span>
              <span className="text-gray-700">{suggestion.signature.slice(typedString.length)}</span>
            </ToolTag>
          </span>
        )}
    </ListItem>
  );
};

const ListItem = ({ children, active, ...props }: React.ComponentProps<typeof ContextMenuListItem>) => {
  // Scroll to suggestion when it becomes active.
  const suggestionRef = useRef<HTMLButtonElement>(null);
  const previousActive = useRef(active);
  useEffect(() => {
    if (active && !previousActive.current) {
      suggestionRef.current?.scrollIntoView({ behavior: "auto", block: "nearest" });
    }
    previousActive.current = active;
  }, [active]);
  return (
    <ContextMenuListItem size={32} className="hl-suggestion" ref={suggestionRef} active={active} {...props}>
      {children}
    </ContextMenuListItem>
  );
};

const getPositionStyles = (selectionState: SelectionState) => {
  if (!selectionState.getHasFocus()) {
    // console.log("selectionState does not have focus");
    return null;
  }
  const selection = window.getSelection();
  if (selection === null) {
    console.log("Selection is null");
    return null;
  }
  if (selection.rangeCount === 0) {
    // console.log("Selection has no range");
    return null;
  }
  // Position of the cursor, relative to the viewport
  const rect = selection.getRangeAt(0).getBoundingClientRect();

  if (rect.top > 0) {
    // If we have a valid rect at the cursor, we just use that (the easy way)
    return { top: rect.top, left: rect.left };
  } else {
    // This would be the case if the cursor is at the start of a block, say (no selection).
    // rect is sometimes {x: 0, y: 0, width: 0, height: 0, top: 0, …}
    // when the cursor is at the start of a block
    const focusNode = selection.focusNode as Element;

    if (!focusNode.getBoundingClientRect) {
      // `focusNode.getBoundingClientRect is not a function` sometimes. Unsure when.
      return null;
    }
    // Client rect is relative to viewport, not page.
    const rect = focusNode.getBoundingClientRect();
    return { top: rect.top, left: rect.left };
  }
};

const addToEditorState = (suggestion: Suggestion, insertFromIndex: number, editorState: EditorState): EditorState => {
  if (suggestion.type === "tool") {
    if (insertFromIndex > 0) {
      return addTextAtCursor(` ${suggestion.signature} }}`, deleteTextAtCursor(insertFromIndex, editorState));
    } else {
      return addTextAtCursor(` ${suggestion.signature} }}`, editorState);
    }
  } else if (suggestion.type === "input") {
    if (insertFromIndex > 0) {
      return addTextAtCursor(` ${suggestion.name} }}`, deleteTextAtCursor(insertFromIndex, editorState));
    } else {
      return addTextAtCursor(` ${suggestion.name} }}`, editorState);
    }
  } else {
    return editorState;
  }
  return editorState;
};

/**
 * Get text in block where cursor is, as well as offset for cursor.
 */
const getBlockTextAndOffset = (editorState: EditorState): { text: string; offset: number } => {
  const selectionState = editorState.getSelection();
  const focusKey = selectionState.getFocusKey();
  const currentContent = editorState.getCurrentContent();
  const currentContentBlock = currentContent.getBlockForKey(focusKey);
  const start = selectionState.getStartOffset();
  const blockText = currentContentBlock.getText();
  return { text: blockText, offset: start };
};
